import { APIURL } from '../config'
import { useEffect, useState, useRef, useCallback } from 'react'

export const INACTIVITY_STORAGE_KEYS = {
  promptScheduled: 'inactivity_prompt_scheduled',
  inactivityScheduled: 'inactivity_scheduled'
}

const DEFAULT_POLLING_INTERVAL_MS = 1000
const DEFAULT_PROMPT_INTERVAL_MS = 1 * 60 * 1000
const DEFAULT_INACTIVITY_INTERVAL_MS = 2 * 60 * 1000

const BROADCAST_CHANNEL_NAME = 'idle-sync'
const BROADCAST_LOGOUT = 'logout'
const BROADCAST_RESET = 'reset'
const BROADCAST_LOGIN = 'login'

/**
 * @param {import('./useDetectIdle.types').UseDetectIdleProps} props
 * TODO: Doesn't currently async API calls...expects actual user activity.
 * Reasonable for most cases...
 */
export const useDetectIdle = ({
  pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
  promptTimeoutMs = DEFAULT_PROMPT_INTERVAL_MS,
  inactivityTimeoutMs = DEFAULT_INACTIVITY_INTERVAL_MS,
  onInactivity = () => {}
}) => {
  const [showPrompt, setShowPrompt] = useState(false)
  const { getLastActiveCookie, resetLastActivityCookie, clearLastActiveCookie } = useLastActivityCookie()

  /** @type {import('react').MutableRefObject<BroadcastChannel | null>} */
  const channelRef = useRef(null)
  if (!channelRef.current) {
    channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
  }
  const channel = channelRef.current

  const getInactivitySchedules = useCallback(() => {
    const promptScheduled = localStorage.getItem(INACTIVITY_STORAGE_KEYS.promptScheduled)
    const inactivityScheduled = localStorage.getItem(INACTIVITY_STORAGE_KEYS.inactivityScheduled)

    return {
      promptScheduled: promptScheduled ? parseInt(promptScheduled, 10) : null,
      inactivityScheduled: inactivityScheduled ? parseInt(inactivityScheduled, 10) : null
    }
  }, [])

  const resetInactivitySchedules = useCallback(() => {
    const now = Date.now()
    const promptSchedule = now + promptTimeoutMs
    const inactivitySchedule = now + inactivityTimeoutMs

    localStorage.setItem(INACTIVITY_STORAGE_KEYS.promptScheduled, promptSchedule.toString())
    localStorage.setItem(INACTIVITY_STORAGE_KEYS.inactivityScheduled, inactivitySchedule.toString())
    resetLastActivityCookie()

    channel.postMessage({ type: BROADCAST_RESET })
  }, [inactivityTimeoutMs, promptTimeoutMs, channel, resetLastActivityCookie])

  const clearInactivitySchedules = useCallback(() => {
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.promptScheduled)
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.inactivityScheduled)
    clearLastActiveCookie()
  }, [clearLastActiveCookie])

  const handleInactivity = useCallback(() => {
    clearInactivitySchedules()
    channel.postMessage({ type: BROADCAST_LOGOUT })
    onInactivity()
  }, [clearInactivitySchedules, onInactivity, channel])

  const checkAndUpdateState = useCallback(
    (isPageLoad = false) => {
      const { promptScheduled, inactivityScheduled } = getInactivitySchedules()

      if (!promptScheduled || !inactivityScheduled) {
        resetInactivitySchedules()
        return
      }

      const now = Date.now()
      const timeUntilPrompt = promptScheduled - now
      const timeUntilInactivity = inactivityScheduled - now
      if (timeUntilInactivity <= 0) {
        if (isPageLoad) {
          const lastActive = getLastActiveCookie()
          if (lastActive && now - lastActive < inactivityTimeoutMs) {
            resetInactivitySchedules()
            return
          }
        }
        handleInactivity()
        return
      }

      if (!isPageLoad && timeUntilPrompt <= 0 && timeUntilInactivity > 0) {
        setShowPrompt(true)
        return
      }

      if (timeUntilPrompt > 0) {
        setShowPrompt(false)
      }
    },
    [getInactivitySchedules, handleInactivity, resetInactivitySchedules, getLastActiveCookie, inactivityTimeoutMs]
  )

  const handleUserActivity = useCallback(() => {
    if (showPrompt) return
    // Only handle activity if prompt is not showing
    resetInactivitySchedules()
  }, [showPrompt, resetInactivitySchedules])

  useEffect(() => {
    /**
     * @param {MessageEvent<{ type: string }>} event
     */
    const handler = (event) => {
      const { type } = event.data || {}

      switch (type) {
        case BROADCAST_RESET:
          checkAndUpdateState()
          break
        case BROADCAST_LOGOUT:
          clearInactivitySchedules()
          onInactivity()
          break
        case BROADCAST_LOGIN:
          resetInactivitySchedules()
          break
        default:
          break
      }
    }

    channel.addEventListener('message', handler)
    return () => channel.removeEventListener('message', handler)
  }, [checkAndUpdateState, onInactivity, resetInactivitySchedules, channel, clearInactivitySchedules])

  const handleStorageChange = useCallback(
    /** @param {StorageEvent} e */
    (e) => {
      const isKeyUpdate = e.key === INACTIVITY_STORAGE_KEYS.promptScheduled || e.key === INACTIVITY_STORAGE_KEYS.inactivityScheduled
      const isUnset = e.oldValue !== null && e.newValue === null

      // Trigger inactivity across tabs via event that detects clearing...
      // Otherwise only triggered in current tab.
      if (isKeyUpdate && isUnset) {
        const { promptScheduled, inactivityScheduled } = getInactivitySchedules()

        if (!promptScheduled && !inactivityScheduled) {
          onInactivity()
          return
        }
      }

      if (isKeyUpdate) {
        checkAndUpdateState()
      }
    },
    [onInactivity, getInactivitySchedules, checkAndUpdateState]
  )

  const onMountRef = useRef(true)

  /** @type {import('react').MutableRefObject<NodeJS.Timeout | null>} */
  const syncIntervalRef = useRef(null)

  useEffect(() => {
    if (onMountRef.current) {
      checkAndUpdateState(true)
      onMountRef.current = false
    }

    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart']
    activityEvents.forEach((event) => window.addEventListener(event, handleUserActivity, { passive: true }))

    window.addEventListener('storage', handleStorageChange)

    syncIntervalRef.current = setInterval(checkAndUpdateState, pollingIntervalMs)

    return () => {
      activityEvents.forEach((event) => window.removeEventListener(event, handleUserActivity))
      window.removeEventListener('storage', handleStorageChange)

      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current)
        syncIntervalRef.current = null
      }
    }
  }, [handleUserActivity, handleStorageChange, checkAndUpdateState, pollingIntervalMs])

  const resetTimers = useCallback(() => {
    resetInactivitySchedules()
    setShowPrompt(false)
  }, [resetInactivitySchedules])

  return {
    showPrompt,
    resetTimers,
    triggerInactivity: handleInactivity
  }
}

// Cookies required to sync idle state across subdomains, which is needed to prevent
// auto logouts when switching between modules and wanting to retain same API token.
// Horrid edge case. Not sure if this invalidates the local storage use above...POC.
// Bit of a hack, but potentially the "simplest" solution without using backend.
const useLastActivityCookie = () => {
  const LAST_ACTIVE_COOKIE_NAME = 'last_active'
  const COOKIE_DOMAIN = getCookieDomainFromApiUrl(APIURL || '')

  const IS_LOCAL = process.env.NODE_ENV === 'development' || !COOKIE_DOMAIN

  const getLastActiveCookie = () => {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LAST_ACTIVE_COOKIE_NAME}=(\\d+)`))
    return match ? parseInt(match[1], 10) : null
  }

  const resetLastActivityCookie = () => {
    const base = `${LAST_ACTIVE_COOKIE_NAME}=${Date.now()}; path=/; max-age=${20 * 60}; SameSite=Lax`
    document.cookie = IS_LOCAL ? base : `${base}; domain=${COOKIE_DOMAIN}; Secure`
  }

  const clearLastActiveCookie = () => {
    const base = `${LAST_ACTIVE_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
    document.cookie = IS_LOCAL ? base : `${base}; domain=${COOKIE_DOMAIN}; Secure`
  }

  return { getLastActiveCookie, resetLastActivityCookie, clearLastActiveCookie }
}

/** @param {string} apiUrl */
const getCookieDomainFromApiUrl = (apiUrl) => {
  if (!apiUrl) return null
  try {
    const hostname = new URL(apiUrl).hostname
    const parts = hostname.split('.')
    if (parts.length < 2) return null
    return `.${parts.slice(-2).join('.')}`
  } catch {
    return null
  }
}
